import { get } from '@ember/helper';
import { noop } from 'ember-composable-helpers/helpers/noop';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ConfirmationModal from '../confirmation-modal.gjs';
import ContactListItem from '../contact-list-item.gjs';
import ConversationDisplayName from '../conversation-display-name.gjs';
import ConversationMemberListItem from '../conversation-member-list-item.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import NegativeButton from 'ember-simplepractice/components/spds/action/button/negative';
import SpdsFeedbackAlertBannerMessage from 'ember-simplepractice/components/spds/feedback/alert-banner/message';
import styles from './conversation-details.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesConversationDetails extends Component {
  @service messaging;
  @service messagingNavigation;

  @tracked showLeaveConfirm = false;

  get routeHandler() {
    return this.args.handler;
  }

  get conversation() {
    return this.routeHandler.conversation;
  }

  get isDraft() {
    return this.routeHandler.isDraft;
  }

  get displayName() {
    return this.conversation.displayName;
  }

  get isClinician() {
    return this.messaging.currentProfile.isClinician;
  }

  get canManageGroupMembers() {
    return this.isClinician && this.conversation.isGroup;
  }

  get showModifyMembers() {
    return this.canManageGroupMembers && this.isDraft;
  }

  get showAddTeamMembers() {
    return this.canManageGroupMembers && !this.isDraft;
  }

  get members() {
    return this.routeHandler.members;
  }

  get memberContacts() {
    if (this.conversation.isCouple) {
      return this.#buildCoupleMemberContacts();
    }

    return this.members.map(member => ({
      isCoupleRow: false,
      id: member.id,
      member,
    }));
  }

  get showViewPreviousMembers() {
    return this.messaging.currentProfile?.isClinician && this.routeHandler.hasPreviousMembers;
  }

  #buildCoupleMemberContacts() {
    const clients = this.members.filter(member => member.isClient);
    const coupleIndex = this.members.findIndex(member => member.isClient);
    const coupleContact = {
      isCoupleRow: true,
      id: this.conversation.linkExternalId,
      members: clients,
      isClient: true,
      isDisabled: clients.some(member => member.isDisabled),
    };

    return this.members
      .filter((member, index) => !member.isClient || index === coupleIndex)
      .map(member =>
        member.isClient ? coupleContact : { isCoupleRow: false, id: member.id, member }
      );
  }

  onNameClickFor = member => {
    if (!this.routeHandler.canNavigateToClientProfile(member)) {
      return undefined;
    }

    return () => {
      document.activeElement.blur();
      this.routeHandler.navigateToClientProfile.perform(member);
    };
  };

  goBack = async () => {
    await this.messagingNavigation.leaveChildRoute();
  };

  viewPreviousMembers = async () => {
    await this.messagingNavigation.toPreviousMembers();
  };

  onModifyMembers = () => {};

  onAddTeamMembers = () => {
    this.messagingNavigation.toAddMembers();
  };

  openLeaveConfirm = () => {
    if (this.routeHandler.isLeaveDisabled) return;
    this.showLeaveConfirm = true;
  };

  closeLeaveConfirm = () => {
    this.showLeaveConfirm = false;
  };

  leaveConversation = async () => {
    await this.routeHandler.leaveConversation();
  };

  <template>
    {{#if this.showLeaveConfirm}}
      <ConfirmationModal
        @title={{t "emc.conversation_details.leave_confirm_question"}}
        @body={{t "emc.conversation_details.leave_confirm_body"}}
        @confirmLabel={{t "emc.conversation_details.leave_conversation"}}
        @onCancel={{this.closeLeaveConfirm}}
        @onConfirm={{this.leaveConversation}}
      />
    {{else}}
      <BaseRoute
        class="{{styles.component}} conversation-details-page"
        data-test-conversation-details-route
      >
        <:header>
          <GhostButton
            @fitContent={{true}}
            @iconOnly={{true}}
            {{on "click" this.goBack}}
            class="back-button"
            data-test-conversation-details-page-back-button
          >
            {{svgJar "messaging-chevron-left"}}
          </GhostButton>

          <span class="title">{{t "emc.conversation_details.title"}}</span>
        </:header>
        <:default>
          {{#if this.isDraft}}
            <div class="draft-banner" data-test-conversation-details-draft-banner>
              <SpdsFeedbackAlertBannerMessage
                @type="info"
                @header={{t "emc.draft_conversation_banner.title"}}
                @body={{t "emc.draft_conversation_banner.description"}}
                @includeIcon={{false}}
              />
            </div>
          {{/if}}

          <ConversationDisplayName
            @displayName={{this.displayName}}
            @editable={{this.isClinician}}
            @onSave={{this.routeHandler.updateDisplayName}}
          />

          <div class="members-section-header" data-test-members-section-header>
            <h2 class="section-title" data-test-members-section-title>
              {{t "emc.conversation_details.members_section" count=this.memberContacts.length}}
            </h2>

            {{#if this.showModifyMembers}}
              <GhostButton
                @size="small"
                class="modify-members-button"
                {{on "click" this.onModifyMembers}}
                data-test-modify-members
              >
                {{svgJar "messaging-users"}}
                {{t "emc.draft_conversation_banner.modify_members"}}
              </GhostButton>
            {{else if this.showAddTeamMembers}}
              <GhostButton
                @size="small"
                class="modify-members-button"
                {{on "click" this.onAddTeamMembers}}
                data-test-add-team-members
              >
                {{svgJar "messaging-users"}}
                {{t "emc.add_team_members.cta"}}
              </GhostButton>
            {{/if}}
          </div>

          <ul class="members-list" data-test-members-list role="list">
            {{#each this.memberContacts key="id" as |memberContact|}}
              {{#if memberContact.isCoupleRow}}
                <ContactListItem
                  @contact={{memberContact}}
                  @onNameClick={{this.onNameClickFor (get memberContact.members 0)}}
                  @showScopeIndicator={{this.isClinician}}
                />
              {{else}}
                <ConversationMemberListItem
                  @member={{memberContact.member}}
                  @onNameClick={{this.onNameClickFor memberContact.member}}
                />
              {{/if}}
            {{/each}}

            {{#if this.showViewPreviousMembers}}
              <li class="view-previous-members">
                <button
                  type="button"
                  data-test-view-previous-members
                  {{on "click" this.viewPreviousMembers}}
                >
                  <span class="view-previous-members-label">
                    {{t "emc.conversation_details.view_previous_members"}}
                  </span>
                  <span class="view-previous-members-chevron" aria-hidden="true">
                    {{svgJar "messaging-chevron-right"}}
                  </span>
                </button>
              </li>
            {{/if}}
          </ul>

          {{#if (or this.routeHandler.showLeaveButton this.isDraft)}}
            <div class="details-action-container" data-test-details-action-container>
              <div class="details-action-wrapper">
                {{#if this.routeHandler.showLeaveButton}}
                  <NegativeButton
                    @fitContentWidth={{true}}
                    @disabled={{this.routeHandler.isLeaveDisabled}}
                    {{on "click" this.openLeaveConfirm}}
                    class="details-action-button leave-conversation-button"
                    data-test-leave-conversation-button
                  >
                    {{t "emc.conversation_details.leave_conversation"}}
                  </NegativeButton>
                  {{#if this.routeHandler.isLeaveDisabled}}
                    <AttachTooltip
                      @placement="bottom"
                      @arrow={{true}}
                      @animation="fade"
                      @class="emc-tooltip leave-disabled-tooltip"
                      @renderInPlace={{true}}
                    >
                      {{t "emc.conversation_details.leave_conversation_disabled_tooltip"}}
                    </AttachTooltip>
                  {{/if}}
                {{else}}
                  <NegativeButton
                    @fitContentWidth={{true}}
                    {{on "click" (noop)}}
                    class="details-action-button discard-draft-button"
                    data-test-discard-draft-button
                  >
                    {{t "emc.conversation_details.discard_draft"}}
                  </NegativeButton>
                {{/if}}
              </div>
            </div>
          {{/if}}
        </:default>
      </BaseRoute>
    {{/if}}
  </template>
}
